import type { Maybe } from '@adornis/base/utilTypes.js';
import type { EntityData } from '@adornis/baseql/entities/adornisEntity.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { ContextPrefix, type AllPermissions } from '../../permissions.js';
import type { ChancenwerkUser } from '../users/entities/ChancenwerkUser.js';
import { KPF } from './entities/KPF.js';

type KPFPermissionMap = Map<AllPermissions, string[]>;

/**
 * User may have specific contexts for permissions. For example if Max Musterman ist LEKO on kpf_x he has the permission to read students on this kpf.
 * In this case the function returns a map with the permission as the key and the id of the kpf in an array for this permission.
 * It returns a map, so we only need to run one request for an array of permissions
 * @param permissions The permissions to look for as an array
 * @param user The user for which to generate the map
 * @returns a Map with the permission as a key and the kpf ids as an array
 */
export async function getKPFAccessibilityMapForPermissions(
  permissions: AllPermissions[],
  user: Maybe<ChancenwerkUser>,
) {
  const resultMap = new Map<string, string[]>() as KPFPermissionMap;
  if (!user) return resultMap;

  const kpfCollection = await getRawCollection<EntityData<KPF>>(KPF._collectionName);

  for (const permission of permissions) {
    const kpfIDs: string[] = [];

    for (const role of user.roles) {
      if (!role.permissions.includes(permission)) continue;

      for (const context of role.contexts) {
        if (context.startsWith(ContextPrefix.KPF)) {
          kpfIDs.push(context.replace(new RegExp(`^${ContextPrefix.KPF}`), ''));
          continue;
        }

        if (context.startsWith(ContextPrefix.SCHOOL)) {
          const kpfs = await kpfCollection
            .find({
              schoolID: context.replace(new RegExp(`^${ContextPrefix.SCHOOL}`), ''),
            })
            .toArray();

          kpfIDs.push(...kpfs.map(kpf => kpf._id));
          continue;
        }

        kpfIDs.push(context);
      }
    }

    resultMap.set(permission, kpfIDs);
  }

  return resultMap;
}
